'use client'

import clsx from 'clsx'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { RadioGroup } from '@headlessui/react'
import { useBooking } from '../../model/bookingContext'
import { BookingStepEnum } from '../../model/bookingTypes'
import { BookingStep } from './BookingStep'
import { Summary } from './Summary'
import { IncompletedStepLabel } from './IncompletedStepLabel'
import { useGetMeetingLink } from '@/shared/api/meeting-link'

export function SpecialitiesStep() {
  const { goToStep, isStepCompleted, state } = useBooking()
  const isCompleted = isStepCompleted(BookingStepEnum.SPECIALITIES)

  if (state.currentStep === BookingStepEnum.SPECIALITIES) {
    return (
      <BookingStep>
        <BookingStep.Title>
          <Trans>Speciality selection</Trans>
        </BookingStep.Title>
        <BookingStep.Content>
          <Specialities meetingLink={state.meetingLinkSlug} />
        </BookingStep.Content>
      </BookingStep>
    )
  }

  if (isCompleted) {
    return <Summary icon="🔍" label={t`Speciality selection`} onEdit={() => goToStep(BookingStepEnum.SPECIALITIES)} text={state.selectedSpeciality ?? ''} />
  }

  return (
    <IncompletedStepLabel icon="🔍">
      <Trans>Speciality selection</Trans>
    </IncompletedStepLabel>
  )
}

type SpecialitiesProps = {
  meetingLink: string
}

function Specialities({ meetingLink: meetingLinkSlug }: SpecialitiesProps) {
  const { meetingLink, isLoadingMeetingLink } = useGetMeetingLink(meetingLinkSlug)
  const { dispatch } = useBooking()

  const specialities = meetingLink?.specialities || []

  const handleSpecialitySelect = (speciality: { code: string; name: string }) => {
    // Store the selected speciality in the context
    dispatch({
      type: 'SET_AVAILABILITY',
      payload: {
        availabilityId: '', // Will be set in the next step
        speciality: speciality.name,
      },
      goToNextStep: true,
    })
  }

  if (isLoadingMeetingLink) {
    return (
      <div className="animate-pulse flex flex-col gap-2">
        <div className="h-12 rounded bg-slate-200" />
        <div className="h-12 rounded bg-slate-200" />
        <div className="h-12 rounded bg-slate-200" />
      </div>
    )
  }

  return (
    <RadioGroup>
      <RadioGroup.Label className="sr-only">
        <Trans>Speciality selection</Trans>
      </RadioGroup.Label>
      <div className="relative -space-y-px rounded-md bg-white">
        {specialities.map((speciality, specialityIdx) => (
          <RadioGroup.Option
            key={speciality.code}
            value={speciality.name}
            data-testid="speciality-card"
            className={({ checked }) =>
              clsx(
                {
                  'rounded-tl-md rounded-tr-md': specialityIdx === 0,
                  'rounded-bl-md rounded-br-md': specialityIdx === specialities.length - 1,
                  'z-10 border-indigo-200 bg-indigo-50': checked,
                  'border-gray-200': !checked,
                },
                'relative flex cursor-pointer flex-col border border-solid p-4 focus:outline-none md:grid md:grid-cols-3 md:pl-4 md:pr-6'
              )
            }
            onClick={() => handleSpecialitySelect(speciality)}
          >
            <RadioGroup.Label as="span" className="text-sm text-gray-900 font-medium">
              {speciality.name}
            </RadioGroup.Label>
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  )
}
